import { Container, Graphics, type Renderer } from 'pixi.js';

import { project, type HeatCell, type Projection } from '@hotlist/shared';

import type { Viewport } from './viewport.ts';

/**
 * The traffic heatmap: where the network reads.
 *
 * A warm glow at every pole, brighter where it reads more -- an additive wash so
 * a cluster of busy cameras lights up the way density should. This is not the
 * road map and not evenly spread: it is a picture of what the network actually
 * sees, which is wherever somebody chose to put a camera and however much runs
 * past it. The dark stretches are not empty roads; they are roads nobody is
 * watching, which is its own kind of information.
 *
 * An operator overlay, above the day/night wash.
 */
export class HeatmapLayer {
  readonly container = new Container();
  private readonly blobs = new Graphics();
  private cells: HeatCell[] = [];
  private maxReads = 1;
  private readonly projection: Projection;

  constructor(_renderer: Renderer, projection: Projection) {
    this.projection = projection;
    this.blobs.blendMode = 'add';
    this.container.addChild(this.blobs);
    this.container.eventMode = 'none';
    this.container.visible = false;
  }

  get active(): boolean {
    return this.cells.length > 0;
  }

  setCells(cells: readonly HeatCell[]): void {
    this.cells = [...cells];
    let max = 1;
    for (const c of this.cells) max = Math.max(max, c.reads);
    this.maxReads = max;
    this.container.visible = this.cells.length > 0;
  }

  clear(): void {
    this.cells = [];
    this.container.visible = false;
    this.blobs.clear();
  }

  update(viewport: Viewport): void {
    if (!this.container.visible || this.cells.length === 0) return;
    this.blobs.clear();
    for (const c of this.cells) {
      const pr = project(this.projection, c.x, c.y);
      const sx = viewport.toScreenX(pr.sx);
      const sy = viewport.toScreenY(pr.sy);
      // Log scale, because a handful of poles read an order of magnitude more
      // than the rest and a linear ramp would leave everything else invisible.
      const t = Math.log1p(c.reads) / Math.log1p(this.maxReads);
      const radius = 18 + 32 * Math.sqrt(t);
      const color = heatColor(t);
      // Concentric fills approximate a soft falloff; additive blend stacks them
      // where poles crowd together.
      this.blobs.circle(sx, sy, radius).fill({ color, alpha: 0.14 });
      this.blobs.circle(sx, sy, radius * 0.6).fill({ color, alpha: 0.22 });
      this.blobs.circle(sx, sy, radius * 0.3).fill({ color, alpha: 0.32 });
    }
  }
}

/** Cool amber (quiet) climbing to bright yellow-white (busiest). */
function heatColor(t: number): number {
  // 0 -> deep orange, 0.5 -> orange, 1 -> pale yellow. Under additive blend the
  // busiest clusters push toward white on their own.
  const lo = [0xd0, 0x50, 0x18];
  const hi = [0xff, 0xe4, 0x90];
  const r = Math.round(lo[0]! + (hi[0]! - lo[0]!) * t);
  const g = Math.round(lo[1]! + (hi[1]! - lo[1]!) * t);
  const b = Math.round(lo[2]! + (hi[2]! - lo[2]!) * t);
  return (r << 16) | (g << 8) | b;
}
